import React from 'react';
import useSeo from '../lib/seo';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, X } from 'lucide-react';
import Reveal from '../components/home/Reveal';
import { scrollToEl } from '../components/home/v3/motion';
import { PhoneFrame, BrowserFrame } from '../components/psicoid/Frames';
import ScreenTour from '../components/psicoid/ScreenTour';
import {
  ICON,
  LINKS,
  HERO_SHOTS,
  FACTS,
  BEFORE,
  AFTER,
  STEPS,
  DIARY_TOUR,
  SOCIAL_TOUR,
  DISCOVER_SHOTS,
  APP_TOUR,
  GALLERY,
  LANDING_SHOTS,
  PALETTE,
  PRINCIPLES,
  STACK,
  DELIVERY,
} from '../data/revyou';

const INDEX = [
  { id: 'o-que-e', label: 'O que é' },
  { id: 'como-funciona', label: 'Como funciona' },
  { id: 'diario', label: 'O diário' },
  { id: 'comunidade', label: 'Comunidade' },
  { id: 'descobrir', label: 'Descobrir' },
  { id: 'app', label: 'App' },
  { id: 'layout', label: 'Layout' },
  { id: 'por-dentro', label: 'Por dentro' },
];

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden>
    <path d="M16.36 12.9c.02-1.86 1.52-2.75 1.58-2.8-.86-1.26-2.2-1.43-2.68-1.45-1.14-.12-2.23.67-2.8.67-.58 0-1.47-.66-2.42-.64-1.24.02-2.39.72-3.03 1.84-1.3 2.25-.33 5.57.93 7.4.62.9 1.35 1.9 2.31 1.86.93-.04 1.28-.6 2.4-.6 1.12 0 1.44.6 2.42.58 1-.02 1.63-.91 2.24-1.81.71-1.04 1-2.05 1.02-2.1-.02-.01-1.96-.75-1.98-2.95zM14.6 7.05c.5-.62.85-1.47.75-2.33-.73.03-1.62.49-2.15 1.1-.47.54-.89 1.42-.78 2.25.82.06 1.66-.41 2.18-1.02z" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden>
    <path d="M3.6 2.3c-.2.2-.32.52-.32.93v17.54c0 .41.12.73.33.93l.06.05 9.83-9.82v-.23L3.66 2.25l-.06.05zM17 16.18l-3.28-3.28v-.23l3.28-3.28.08.04 3.88 2.2c1.11.63 1.11 1.66 0 2.3l-3.88 2.2-.08.05zM13.72 12.9L3.6 23.02c.37.38.97.43 1.65.05l11.6-6.59-3.13-3.58zM5.25.93C4.57.55 3.97.6 3.6.98L13.72 11.1l3.13-3.58L5.25.93z" />
  </svg>
);

function StoreLinks() {
  return (
    <>
      <a className="btn-ghost pid-store" href={LINKS.appStore} target="_blank" rel="noopener noreferrer">
        <AppleIcon /> App Store
      </a>
      <a className="btn-ghost pid-store" href={LINKS.googlePlay} target="_blank" rel="noopener noreferrer">
        <PlayIcon /> Google Play
      </a>
    </>
  );
}

function ShotGrid({ items }) {
  return (
    <div className="pid-public">
      {items.map((s, i) => (
        <Reveal className="pid-public-item" key={s.id} delay={i * 120}>
          <BrowserFrame src={s.img} alt={s.alt} url={s.url} />
          <h3>{s.title}</h3>
          <p>{s.desc}</p>
        </Reveal>
      ))}
    </div>
  );
}

function Revyou() {
  useSeo({
    title: 'Revyou: diário de games e filmes na web, iOS e Android | Idealizer',
    description:
      'Revyou é a rede para registrar, avaliar e relembrar tudo o que você joga e assiste: biblioteca, reviews, listas, feed de amigos e importação de Steam, PSN e Xbox. Veja como funciona e como é o layout. Projeto da Idealizer.',
    path: '/apps/revyou',
  });

  return (
    <main className="revyou-page">
      {/* ───────── Hero ───────── */}
      <section className="pid-hero">
        <div className="pid-hero-glow" aria-hidden />
        <div className="container">
          <Link to="/nosso-trabalho" className="pg-back">
            <ArrowLeft size={16} strokeWidth={2} aria-hidden /> Nosso trabalho
          </Link>

          <div className="pid-hero-grid">
            <div className="pid-hero-head">
              <span className="section-eyebrow pid-eyebrow">Games e filmes · Web, iOS e Android</span>
              <h1 className="pid-hero-title ry-title">
                <img src={ICON} alt="" className="ry-star" width="96" height="96" />
                <span>Revyou</span>
              </h1>
              <p className="pid-hero-tagline">Registre. Avalie. Relembre.</p>
              <p className="pid-hero-sub">
                Um diário para tudo o que você joga e assiste. Cada jogo terminado e cada filme visto
                ficam guardados com a nota, a review e a data, e os amigos acompanham pelo feed.
              </p>
              <div className="pid-hero-actions">
                <a className="btn-primary" href={LINKS.site} target="_blank" rel="noopener noreferrer">
                  Visitar o site <ArrowUpRight size={18} strokeWidth={2} aria-hidden />
                </a>
                <StoreLinks />
              </div>
            </div>

            {/* Visual principal: sem Reveal, para aparecer de imediato acima da dobra. */}
            <div className="pid-hero-stage">
              <div className="pid-hero-blob" aria-hidden />
              <BrowserFrame
                className="pid-hero-browser"
                src={HERO_SHOTS.browser}
                alt="Perfil de uma usuária no Revyou web, com os jogos favoritos"
                url="revyouapp.com/pt/u/marina/profile"
                eager
              />
              <PhoneFrame
                className="pid-hero-phone"
                src={HERO_SHOTS.phone}
                alt="Página de um jogo no app Revyou"
                eager
              />
            </div>
          </div>

          <dl className="pid-facts">
            {FACTS.map((f) => (
              <div className="pid-fact" key={f.label}>
                <dt>{f.label}</dt>
                <dd>{f.value}</dd>
              </div>
            ))}
          </dl>

          <nav className="pid-index" aria-label="Nesta página">
            {INDEX.map((item) => (
              <button type="button" key={item.id} onClick={() => scrollToEl(`#${item.id}`)}>
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* ───────── O que é ───────── */}
      <section className="pid-section" id="o-que-e">
        <div className="container pid-about">
          <Reveal className="pid-about-copy">
            <span className="section-eyebrow pid-eyebrow">O que é</span>
            <h2 className="section-title">A memória de tudo o que você jogou e assistiu.</h2>
            <p className="pid-lead">
              Quem joga muito esquece muito: o que achou daquele RPG de três anos atrás, em que
              plataforma parou, qual era o próximo da fila. As bibliotecas ficam espalhadas entre
              lojas e consoles, e a opinião, em conversas que ninguém acha depois.
            </p>
            <p className="pid-lead">
              O Revyou junta isso num lugar só. É catálogo, diário e rede social: você registra
              jogos e filmes com status, nota e review, organiza em listas, e vê pelo feed o que
              os amigos estão jogando e o que acharam.
            </p>
          </Reveal>

          <Reveal className="pid-compare" delay={120}>
            <div className="pid-compare-col pid-compare-col--before">
              <h3>Sem o Revyou</h3>
              <ul>
                {BEFORE.map((t) => (
                  <li key={t}>
                    <X size={16} strokeWidth={2.2} aria-hidden /> <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="pid-compare-col pid-compare-col--after">
              <h3>Com o Revyou</h3>
              <ul>
                {AFTER.map((t) => (
                  <li key={t}>
                    <Check size={16} strokeWidth={2.4} aria-hidden /> <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────── Como funciona ───────── */}
      <section className="pid-section pid-section--soft" id="como-funciona">
        <div className="container">
          <Reveal className="section-head">
            <span className="section-eyebrow pid-eyebrow">Como funciona</span>
            <h2 className="section-title">Do título à review em quatro passos.</h2>
            <p className="section-sub">
              Registrar leva segundos. O que vem depois, biblioteca, linha do tempo e recap, o
              Revyou monta sozinho.
            </p>
          </Reveal>

          <ol className="pid-steps">
            {STEPS.map((s, i) => (
              <Reveal as="li" className="pid-step" key={s.n} delay={i * 90}>
                <div className="pid-step-copy">
                  <span className="pid-step-n" aria-hidden>
                    {s.n}
                  </span>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
                <div className="pid-step-media">
                  <PhoneFrame src={s.img} alt={s.alt} />
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* ───────── Web: o diário ───────── */}
      <section className="pid-section pid-persona ry-persona--games" id="diario">
        <div className="container">
          <Reveal className="section-head">
            <span className="section-eyebrow pid-persona-eyebrow">
              <span className="pid-dot" aria-hidden /> Na web · o diário
            </span>
            <h2 className="section-title">Cada jogo com nota, data e história.</h2>
            <p className="section-sub">
              Da grade de capas à review publicada: é aqui que a biblioteca vira memória. Escolha
              uma tela para ver.
            </p>
          </Reveal>
          <Reveal>
            <ScreenTour items={DIARY_TOUR} frame="browser" label="Telas de registro do Revyou web" />
          </Reveal>
        </div>
      </section>

      {/* ───────── Web: comunidade ───────── */}
      <section className="pid-section pid-section--soft pid-persona ry-persona--films" id="comunidade">
        <div className="container">
          <Reveal className="section-head">
            <span className="section-eyebrow pid-persona-eyebrow">
              <span className="pid-dot" aria-hidden /> Na web · a comunidade
            </span>
            <h2 className="section-title">Jogar sozinho, comentar junto.</h2>
            <p className="section-sub">
              Seguir alguém no Revyou é receber as reviews, as listas e os jogos que essa pessoa
              começou. A opinião de quem você conhece pesa mais que a média.
            </p>
          </Reveal>
          <Reveal>
            <ScreenTour items={SOCIAL_TOUR} frame="browser" label="Telas sociais do Revyou web" />
          </Reveal>
        </div>
      </section>

      {/* ───────── Descobrir ───────── */}
      <section className="pid-section pid-persona ry-persona--games" id="descobrir">
        <div className="container">
          <Reveal className="section-head">
            <span className="section-eyebrow pid-persona-eyebrow">
              <span className="pid-dot" aria-hidden /> Catálogo
            </span>
            <h2 className="section-title">O próximo jogo já está no catálogo.</h2>
            <p className="section-sub">
              Mais de 320 mil jogos e 360 mil filmes para buscar, ranquear e trazer de outras
              plataformas.
            </p>
          </Reveal>
          <ShotGrid items={DISCOVER_SHOTS} />
        </div>
      </section>

      {/* ───────── App ───────── */}
      <section className="pid-section pid-section--soft pid-persona ry-persona--films" id="app">
        <div className="container">
          <Reveal className="section-head">
            <span className="section-eyebrow pid-persona-eyebrow">
              <span className="pid-dot" aria-hidden /> App · iOS e Android
            </span>
            <h2 className="section-title">O catálogo no bolso.</h2>
            <p className="section-sub">
              A mesma conta e os mesmos dados da web, num app nativo: terminou o jogo, registra
              antes de largar o controle.
            </p>
          </Reveal>
          <Reveal>
            <ScreenTour items={APP_TOUR} label="Telas do app Revyou" reverse />
          </Reveal>
          <Reveal className="ry-app-stores">
            <StoreLinks />
          </Reveal>
        </div>
      </section>

      {/* ───────── Layout ───────── */}
      <section className="pid-section" id="layout">
        <div className="container">
          <Reveal className="section-head">
            <span className="section-eyebrow pid-eyebrow">Layout</span>
            <h2 className="section-title">Escuro, para a capa aparecer.</h2>
            <p className="section-sub">
              O Revyou tem sistema de design próprio, compartilhado entre o site e o app. No
              navegador do celular o site se rearranja sozinho: a lateral vira barra inferior e
              tudo continua a um toque.
            </p>
          </Reveal>
        </div>

        <Reveal className="pid-gallery" role="group" aria-label="Telas do Revyou web no celular">
          <ul className="pid-gallery-track" tabIndex={0}>
            {GALLERY.map((g) => (
              <li key={g.label}>
                <PhoneFrame src={g.img} alt={`Tela ${g.label} do Revyou no navegador do celular`} />
                <span>{g.label}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        <div className="container">
          <ShotGrid items={LANDING_SHOTS} />

          <div className="pid-system">
            <Reveal className="pid-system-card">
              <h3 className="pid-system-label">Cores</h3>
              <ul className="pid-palette">
                {PALETTE.map((c) => (
                  <li key={c.hex}>
                    <span
                      className={`pid-swatch ${c.dark ? 'is-dark' : ''}`}
                      style={{ background: c.hex }}
                      aria-hidden
                    >
                      {c.hex}
                    </span>
                    <strong>{c.name}</strong>
                    <span>{c.role}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal className="pid-system-card pid-type" delay={120}>
              <h3 className="pid-system-label">Tipografia</h3>
              <p className="pid-type-sample" aria-hidden>
                Aa
              </p>
              <p className="pid-type-name">Space Grotesk</p>
              <ul className="pid-type-weights" aria-label="Pesos usados">
                <li style={{ fontWeight: 700 }}>Bold · títulos e números</li>
                <li style={{ fontWeight: 500 }}>Medium · rótulos e botões</li>
                <li style={{ fontWeight: 400 }}>Regular · texto e reviews</li>
              </ul>
            </Reveal>
          </div>

          <ul className="pid-principles">
            {PRINCIPLES.map((p, i) => (
              <Reveal as="li" key={p.title} delay={i * 80}>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </Reveal>
            ))}
          </ul>

          <p className="pid-note">
            As telas desta página usam membros, notas e reviews fictícios. Os jogos e os filmes são
            os do catálogo, com capas da IGDB e da TheTVDB.
          </p>
        </div>
      </section>

      {/* ───────── Por dentro ───────── */}
      <section className="pid-section pid-section--soft" id="por-dentro">
        <div className="container">
          <Reveal className="section-head">
            <span className="section-eyebrow pid-eyebrow">Por dentro</span>
            <h2 className="section-title">Três frentes, um time.</h2>
            <p className="section-sub">
              Site, app e backend foram desenhados e desenvolvidos aqui, e seguem sendo evoluídos
              pelo mesmo time.
            </p>
          </Reveal>

          <ul className="pid-stack">
            {STACK.map((s, i) => (
              <Reveal as="li" className="pid-stack-card" key={s.title} delay={i * 100}>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <ul className="pid-tags" aria-label="Tecnologias">
                  {s.tags.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </ul>

          <Reveal className="pid-delivery">
            <h3>O que a Idealizer fez</h3>
            <ul>
              {DELIVERY.map((d) => (
                <li key={d}>
                  <Check size={16} strokeWidth={2.4} aria-hidden /> {d}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ───────── CTA ───────── */}
      <section className="pid-cta">
        <div className="container">
          <Reveal className="cta-card">
            <div className="cta-glow" aria-hidden />
            <h2 className="cta-title">Quer um produto como o Revyou?</h2>
            <p className="cta-sub">
              Da web ao mobile, projetamos, desenvolvemos e mantemos plataformas sociais
              completas. Vamos tirar a sua ideia do papel.
            </p>
            <div className="cta-actions">
              <Link to="/contato" className="btn-primary">
                Começar um projeto <ArrowRight size={18} strokeWidth={2} aria-hidden />
              </Link>
              <a className="btn-ghost" href={LINKS.site} target="_blank" rel="noopener noreferrer">
                Conhecer o Revyou
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

export default Revyou;
